// Mifflin/Harris-Benedict style BMR + daily target based on goal
// Mifflin-St Jeor BMR
export function computeBMR({ weight, height, age, sex }) {
  const w = parseFloat(weight) || 0;
  const h = parseFloat(height) || 0;
  const a = parseFloat(age) || 0;
  if (sex === "M") {
    return 10 * w + 6.25 * h - 5 * a + 5;
  }
  return 10 * w + 6.25 * h - 5 * a - 161;
}

export const ACTIVITY_LEVELS = [
  { value: "sedentario", label: "Sedentario (lavoro d'ufficio, zero sport)", factor: 1.2 },
  { value: "leggero", label: "Leggermente attivo (1-3 allenamenti/sett.)", factor: 1.375 },
  { value: "moderato", label: "Moderatamente attivo (3-5 allenamenti/sett.)", factor: 1.55 },
  { value: "attivo", label: "Molto attivo (6-7 allenamenti/sett.)", factor: 1.725 },
  { value: "atleta", label: "Estremamente attivo (atleta / lavoro fisico)", factor: 1.9 },
];

export function activityFactor(value) {
  return (ACTIVITY_LEVELS.find((a) => a.value === value) || ACTIVITY_LEVELS[0]).factor;
}

export function computeTDEE(profile) {
  return Math.round(computeBMR(profile) * activityFactor(profile.activity));
}

// Minimum recommended hydration in liters (35 ml per kg)
export function computeHydration(weight) {
  const w = parseFloat(weight) || 0;
  return Math.round((w * 35) / 10) / 100;
}

const GOAL_ADJUST = {
  "Perdere Peso": -500,
  Mantenimento: 0,
  "Aumento Massa": 350,
  Generale: 0,
};

export function computeTarget(profile) {
  const tdee = computeTDEE(profile);
  const adjust = GOAL_ADJUST[profile.goal] ?? 0;
  return Math.max(1000, Math.round(tdee + adjust));
}

// Macro split: 50% carboidrati, 30% grassi, 20% proteine
export function computeMacroTargets(kcal) {
  return {
    carbs: Math.round((kcal * 0.5) / 4),
    fat: Math.round((kcal * 0.3) / 9),
    protein: Math.round((kcal * 0.2) / 4),
  };
}

export const ALCOHOL_TYPES = [
  { value: "birra", label: "Birra (5% Vol)", abv: 5 },
  { value: "vino", label: "Vino (12% Vol)", abv: 12 },
  { value: "aperitivo", label: "Aperitivo / Liquore dolce (20% Vol)", abv: 20 },
  { value: "superalcolico", label: "Superalcolico / Liquore (40% Vol)", abv: 40 },
];

// Alcol puro (g) = ml * abv% * 0.789 (densità etanolo); kcal vuote = g * 7
export function computeAlcohol(volumeMl, abv) {
  const ml = parseFloat(volumeMl) || 0;
  const grams = ml * (abv / 100) * 0.789;
  return { grams: Math.round(grams * 10) / 10, kcal: Math.round(grams * 7) };
}

export const MACRO_AREAS = [
  { value: "peso", label: "Gestione del Peso", conditions: ["Perdere peso", "Mantenimento del peso", "Aumento massa muscolare"] },
  { value: "allergie", label: "Intolleranze Digestive e Allergie", conditions: ["Celiachia / Glutine", "Intolleranza al Lattosio", "Allergia Proteine del Latte", "Allergia Frutta a Guscio", "Allergia Crostacei", "Allergia Uova"] },
  { value: "metabolico", label: "Equilibrio Metabolico", conditions: ["Controllo Zuccheri / Glicemia", "Supporto Metabolismo Lento", "Ritenzione Idrica"] },
  { value: "comportamento", label: "Abitudini e Comportamento", conditions: ["Gestione Attacchi di Fame", "Alimentazione Controllata", "Alimentazione Selettiva"] },
];

// Fascia oraria notturna 21:00 -> 05:00
export function isNight() {
  const h = new Date().getHours();
  return h >= 21 || h < 5;
}

export function isToday(ts) {
  const d = new Date(ts);
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
}

// Mappa etichetta allergene -> RADICI (stem) alimentari, così singolari/plurali/accrescitivi
// (es. gambero/gamberi/gamberone/gamberetti) vengono tutti riconosciuti con match a prefisso.
const ALLERGEN_KEYWORDS = {
  "Glutine": ["glutine", "farin", "frument", "grano", "pane", "pasta", "pangratt", "orzo", "farro", "couscous", "seitan", "bulgur", "biscott", "pizza", "cracker", "grissin", "piadin", "focacc"],
  "Lattosio": ["latte", "lattic", "burro", "formagg", "panna", "yogurt", "mozzarell", "parmigian", "ricott", "mascarpone", "besciamell", "gelato", "cheddar", "stracchin", "gorgonzol", "provol", "scamorz", "fontin", "caciott", "pecorin"],
  "Noci / Frutta a guscio": ["noci", "noce", "mandorl", "nocciol", "pistacch", "anacard", "pinol", "pecan"],
  "Crostacei": ["crostace", "gamber", "scamp", "aragost", "granch", "granc", "mazzancoll", "astic", "canocch", "canolicch"],
  "Uova": ["uovo", "uova", "tuorl", "albume", "maiones", "frittat", "omelette", "zabaion"],
  "Soia": ["soia", "edamame", "tofu", "tempeh"],
  "Pesce": ["pesce", "tonno", "salmon", "merluzz", "acciug", "alici", "sgombr", "branzin", "orat", "sardin", "baccal", "nasell", "platess", "ostrich", "molluschi", "vongol", "cozz", "calamar", "seppi", "polpo", "totan"],
  "Arachidi": ["arachid", "nocciolin"],
  "Sesamo": ["sesamo", "tahin"],
};

function _escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Normalizza un'etichetta di allergene del profilo alla chiave usata in ALLERGEN_KEYWORDS.
// Così sia "Crostacei" sia "Allergia Crostacei" (o la condizione specifica del profilo)
// vengono riconosciute con le stesse parole chiave.
function _keywordsFor(label) {
  if (!label) return null;
  if (ALLERGEN_KEYWORDS[label]) return { key: label, kws: ALLERGEN_KEYWORDS[label] };
  const norm = String(label)
    .replace(/celiachia\s*\/?\s*glutine/i, "Glutine")
    .replace(/intolleranza al lattosio/i, "Lattosio")
    .replace(/allergia proteine del latte/i, "Lattosio")
    .replace(/allergia frutta a guscio/i, "Noci / Frutta a guscio")
    .replace(/^(allergia|intolleranza)\s+(al|alla|ai|alle|allo|agli|d'|di)?\s*/i, "")
    .trim();
  const key = Object.keys(ALLERGEN_KEYWORDS).find((k) => k.toLowerCase() === norm.toLowerCase());
  if (key) return { key, kws: ALLERGEN_KEYWORDS[key] };
  return null;
}

// Confronta le intolleranze del profilo con una lista di testi (nome piatto + ingredienti).
// Restituisce l'elenco delle etichette di allergeni rilevati.
export function detectAllergens(profile, texts) {
  const hay = (texts || []).filter(Boolean).map((t) => String(t).toLowerCase()).join(" | ");
  if (!hay) return [];
  const found = new Set();
  // Considera sia le allergie selezionate sia la condizione specifica scelta nel profilo.
  const labels = [...(profile?.allergies || [])];
  if (profile?.condition) labels.push(profile.condition);
  labels.forEach((a) => {
    if (!a || a === "Altro") return;
    const match = _keywordsFor(a);
    const kws = match ? match.kws : [a.toLowerCase()];
    const displayKey = match ? match.key : a;
    if (kws.some((k) => new RegExp(`\\b${_escapeRe(k)}`, "i").test(hay))) found.add(displayKey);
  });
  const other = (profile?.allergyOther || "").trim().toLowerCase();
  if (other) {
    const stem = (w) => w.replace(/(oni|one|etti|etto|ini|ino|acci|astri)$/, "").replace(/[oaei]$/, "");
    const tokens = other.split(/[,;\s]+/).filter((w) => w.length >= 3).map(stem).filter((w) => w.length >= 3);
    if (tokens.some((t) => new RegExp(`\\b${_escapeRe(t)}`, "i").test(hay))) {
      found.add(profile.allergyOther.trim());
    }
  }
  return [...found];
}

// Parole chiave per le diete
const MEAT_KEYWORDS = ["carne", "manzo", "pollo", "pollame", "maiale", "suino", "prosciutto", "salame", "salsiccia", "bresaola", "tacchino", "vitello", "agnello", "coniglio", "wurstel", "würstel", "speck", "pancetta", "bacon", "mortadella", "hamburger", "polpette", "cotoletta", "cinghiale", "anatra", "ragù", "guanciale", "lardo", "carpaccio"];
const FISH_KEYWORDS = ["pesce", "tonno", "salmone", "merluzzo", "acciughe", "acciuga", "alici", "sgombro", "branzino", "orata", "gambero", "gamberi", "gamberoni", "gamberetti", "scampi", "crostacei", "calamari", "calamaro", "seppia", "polpo", "vongole", "cozze", "frutti di mare", "aragosta", "granchio", "baccalà", "sardine", "sardina", "nasello", "platessa", "ostriche", "molluschi"];
const ANIMAL_KEYWORDS = ["latte", "burro", "formaggio", "formaggi", "panna", "yogurt", "mozzarella", "parmigiano", "grana", "ricotta", "mascarpone", "uovo", "uova", "miele", "gelato", "besciamella", "stracchino", "gorgonzola"];

// Motore unificato: restituisce tutti gli avvisi (intolleranze + dieta) per i testi dati.
export function getDietaryWarnings(profile, texts) {
  const warnings = [];
  detectAllergens(profile, texts).forEach((a) => warnings.push(`Contiene ${a} (tua intolleranza)`));
  const hay = (texts || []).filter(Boolean).map((t) => String(t).toLowerCase()).join(" | ");
  const hasAny = (arr) => arr.some((k) => new RegExp(`\\b${_escapeRe(k)}`, "i").test(hay));
  const diet = (profile?.diet || "").toLowerCase();
  if (hay) {
    if (diet === "vegetariana" && (hasAny(MEAT_KEYWORDS) || hasAny(FISH_KEYWORDS))) {
      warnings.push("Non adatto alla dieta vegetariana (contiene carne o pesce)");
    }
    if (diet === "vegana" && (hasAny(MEAT_KEYWORDS) || hasAny(FISH_KEYWORDS) || hasAny(ANIMAL_KEYWORDS))) {
      warnings.push("Non adatto alla dieta vegana (contiene alimenti di origine animale)");
    }
  }
  return warnings;
}

export const HABIT_ADVICE = {
  restrittivo: {
    label: "Alimentazione Controllata",
    advice:
      "Mantieni pasti regolari e bilanciati. Evita restrizioni eccessive: la costanza batte l'estremismo.",
    stats: [
      ["Pasti/die", "3-4"],
      ["Deficit", "moderato"],
    ],
  },
  iperfagia: {
    label: "Gestione Attacchi di Fame",
    advice:
      "Aumenta fibre e proteine per aumentare la sazietà. Bevi acqua prima dei pasti e pianifica spuntini sani.",
    stats: [
      ["Proteine", "alte"],
      ["Fibre", "30g+"],
    ],
  },
  selettivo: {
    label: "Alimentazione Selettiva",
    advice:
      "Introduci gradualmente nuovi alimenti. Varia colori e consistenze per coprire i micronutrienti.",
    stats: [
      ["Varietà", "chiave"],
      ["Micro", "completi"],
    ],
  },
};

export const ENERGY_ADVICE = {
  zuccheri: {
    label: "Controllo Zuccheri",
    advice:
      "Preferisci carboidrati complessi e a basso indice glicemico. Abbina sempre proteine o grassi buoni.",
    stats: [
      ["Zuccheri", "< 25g"],
      ["Indice GL", "basso"],
    ],
  },
  rallentato: {
    label: "Supporto Metabolismo",
    advice:
      "Non saltare i pasti, idratati bene e includi attività fisica quotidiana per sostenere il metabolismo.",
    stats: [
      ["Acqua", "2L+"],
      ["Movimento", "8k passi"],
    ],
  },
};

export const INTOLERANCE_ADVICE = {  glutine: {
    label: "Esclusione Glutine / Celiachia",
    advice:
      "Scegli cereali naturalmente senza glutine (riso, mais, quinoa, grano saraceno). Controlla le etichette.",
    stats: [
      ["Evita", "frumento"],
      ["OK", "riso, mais"],
    ],
  },
  lattosio: {
    label: "Esclusione Lattosio",
    advice:
      "Opta per prodotti delattosati o bevande vegetali. Formaggi stagionati sono spesso ben tollerati.",
    stats: [
      ["Evita", "latte fresco"],
      ["OK", "stagionati"],
    ],
  },
  latte: {
    label: "Esclusione Proteine Latte",
    advice:
      "Elimina tutti i derivati del latte. Integra calcio da vegetali a foglia verde e bevande fortificate.",
    stats: [
      ["Calcio", "vegetale"],
      ["Evita", "caseina"],
    ],
  },
};

// Beverage catalog with kcal estimates and awareness/hydration suggestions.
export const BEVERAGES = [
  { id: "caffe", name: "Caffè", icon: "coffee", kcal: 2, awareness: "Bevi un bicchiere d'acqua dopo il caffè per reidratarti e ridurre l'effetto diuretico." },
  { id: "cappuccino", name: "Cappuccino", icon: "coffee", kcal: 60, awareness: "Ottimo a colazione. Nel pomeriggio preferisci un caffè per limitare gli zuccheri del latte." },
  { id: "te-verde", name: "Tè verde", icon: "cup", kcal: 0, awareness: "Scelta idratante e ricca di antiossidanti. Perfetta senza zucchero aggiunto." },
  { id: "succo", name: "Succo di frutta", icon: "soda", kcal: 110, awareness: "Meglio la frutta intera: più fibre e più sazietà rispetto al succo zuccherato." },
  { id: "bibita", name: "Bibita zuccherata", icon: "soda", kcal: 140, awareness: "Prova acqua frizzante con limone o lime: stessa freschezza, zero zuccheri." },
  { id: "aperitivo", name: "Aperitivo (Spritz)", icon: "wine", kcal: 180, awareness: "Preferisci un analcolico amaro o un calice di vino secco: meno zuccheri dell'aperitivo classico." },
  { id: "vino", name: "Vino rosso", icon: "wine", kcal: 125, awareness: "Con moderazione. Alterna ogni calice con un bicchiere d'acqua." },
  { id: "birra", name: "Birra", icon: "beer", kcal: 150, awareness: "Una piccola alla spina ha meno calorie. Accompagna sempre con acqua." },
  { id: "acqua", name: "Acqua", icon: "water", kcal: 0, awareness: "Ottimo! L'idratazione è la base di ogni buona abitudine alimentare." },
];

